import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Coordinate } from 'ol/coordinate';
import { transform } from 'ol/proj';
import axiosInstance from '../axiosIntance';
import NewBienOffMarket from '../actions/NewBienOffMarket';

export interface ContextMenuState {
  visible: boolean;
  x: number;
  y: number;
  coordinate: Coordinate | null;
  identifiedParcelles: any[];
  sitgUrl: string | null;
}

export interface PropertyData {
  terrain_area_m2: number | null;
  building_type: string | null;
  building_area_m2: number | null;
  data_sources: string[];
}

export interface MapContextMenuProps {
  contextMenu: ContextMenuState;
  onContextMenuChange: (contextMenu: ContextMenuState) => void;
  onRemoveIdentifiedParcelles: () => void;
}

const MapContextMenu: React.FC<MapContextMenuProps> = ({
  contextMenu,
  onContextMenuChange,
  onRemoveIdentifiedParcelles,
}) => {
  const [showNewBienOffMarket, setShowNewBienOffMarket] = React.useState(false);
  const [savedParcelleData, setSavedParcelleData] = React.useState<any>(null);
  const [propertyData, setPropertyData] = React.useState<PropertyData | null>(null);
  const [loadingPropertyData, setLoadingPropertyData] = React.useState(false);
  
  // Close context menu when clicking outside the context menu
  useEffect(() => {
    const handleGlobalClick = (event: MouseEvent) => {
      // Don't close if clicking on the context menu itself
      const target = event.target as HTMLElement;
      if (target.closest('[data-context-menu]')) {
        return;
      }
      
      // Close context menu and remove parcelles
      onContextMenuChange({ 
        visible: false, 
        x: 0, 
        y: 0, 
        coordinate: null, 
        identifiedParcelles: [], 
        sitgUrl: null 
      });
      onRemoveIdentifiedParcelles();
    };

    if (contextMenu.visible) {
      // Use capture phase to ensure we catch the event before other handlers
      document.addEventListener('click', handleGlobalClick, true);
      return () => document.removeEventListener('click', handleGlobalClick, true);
    }
  }, [contextMenu.visible, onContextMenuChange, onRemoveIdentifiedParcelles]);

  // Function to handle SITG identify link
  const handleSitgIdentify = () => {
    const { identifiedParcelles, sitgUrl } = contextMenu;
    
    if (identifiedParcelles.length > 0) {
      // Show filtered parcelles information
      const parcelleInfo = identifiedParcelles.map((p: any) => 
        `Parcelle ${p.value} (${p.attributes?.COMMUNE || 'Unknown'})`
      ).join('\n');
      
      const userChoice = window.confirm(
        `Parcelles trouvées:\n${parcelleInfo}\n\nVoulez-vous ouvrir la page SITG Identify?`
      );
      
      if (userChoice && sitgUrl) {
        window.open(sitgUrl, '_blank');
      }
    } else if (sitgUrl) {
      // No filtered results, but we have the URL - open it anyway
      const userChoice = window.confirm(
        'Aucune parcelle trouvée à cet endroit exact.\nVoulez-vous ouvrir SITG Identify pour voir la zone?'
      );
      
      if (userChoice) {
        window.open(sitgUrl, '_blank');
      }
    } else {
      alert('Aucune donnée SITG disponible pour cette location');
    }
    
    // Close context menu and remove parcelles
    onContextMenuChange({ 
      visible: false, 
      x: 0, 
      y: 0, 
      coordinate: null, 
      identifiedParcelles: [], 
      sitgUrl: null 
    });
    onRemoveIdentifiedParcelles();
  };

  // Function to handle registre foncier link
  const handleRegistreFoncier = () => {
    const { identifiedParcelles } = contextMenu;
    
    if (identifiedParcelles.length > 0) {
      // Extract commune and parcelle numbers from identified parcelles
      const parcelleUrls = identifiedParcelles.map((p: any) => {
        const noParcelle = p.value; // e.g., "7340"
        const noCommune = p.attributes?.['NO COMMUNE']; // e.g., "21"
        
        if (noParcelle && noCommune) {
          return `https://ge.ch/terextraitfoncier/rapport.aspx?commune=${noCommune}&parcelle=${noParcelle}`;
          //return `https://melcarne.digipulse.ch/collections/registre_foncier?filter=NoCommune%3A%3A%3A${noCommune}&filter=NoParcelle%3A%3A%3A${noParcelle}&page=1&limit=50`;
        }
        return null;
      }).filter((url): url is string => url !== null);
      
      if (parcelleUrls.length > 0) {
        const parcelleInfo = identifiedParcelles.map((p: any) => 
          `Parcelle ${p.value} (Commune ${p.attributes?.['NO COMMUNE'] || 'Unknown'})`
        ).join('\n');
        parcelleUrls.forEach(url => {
          window.open(url, '_blank');
        });
      } else {
        alert('Aucune donnée de parcelle disponible pour cette location');
      }
    } else {
      alert('Aucune parcelle identifiée pour cette location');
    }
    
    // Close context menu and remove parcelles
    onContextMenuChange({ 
      visible: false, 
      x: 0, 
      y: 0, 
      coordinate: null, 
      identifiedParcelles: [], 
      sitgUrl: null 
    });
    onRemoveIdentifiedParcelles();
  };

  // Function to handle creating new bien off-market
  const handleCreateBienOffMarket = () => {
    // Save parcelle data before closing context menu
    const parcelleData = getParcelleDataForBienOffMarket();
    setSavedParcelleData(parcelleData);
    setShowNewBienOffMarket(true);
    
    // Close context menu
    onContextMenuChange({ 
      visible: false, 
      x: 0, 
      y: 0, 
      coordinate: null, 
      identifiedParcelles: [], 
      sitgUrl: null 
    });
    onRemoveIdentifiedParcelles();
  };

  // Function to fetch property data automatically
  const fetchPropertyData = React.useCallback(async () => {
    if (!contextMenu.coordinate) return;
    
    setLoadingPropertyData(true);
    try {
      const [lon, lat] = transform(contextMenu.coordinate, "EPSG:3857", "EPSG:4326");
      
      const response = await axiosInstance.get(
        `https://melcarnebackend.digipulse.ch/get_property_data?lon=${lon}&lat=${lat}&parcelle_data=${encodeURIComponent(JSON.stringify(contextMenu.identifiedParcelles))}`
      );
      
      if (response.data?.success && response.data?.data) {
        setPropertyData(response.data.data);
        console.log(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching property data:', error);
      setPropertyData(null);
    } finally {
      setLoadingPropertyData(false);
    }
  }, [contextMenu.coordinate, contextMenu.identifiedParcelles]);

  // Fetch property data when context menu becomes visible
  useEffect(() => {
    if (contextMenu.visible && contextMenu.coordinate) {
      fetchPropertyData();
    } else {
      setPropertyData(null);
    }
  }, [contextMenu.visible, contextMenu.coordinate, fetchPropertyData]);

  // Prepare parcelle data for NewBienOffMarket component
  const getParcelleDataForBienOffMarket = () => {
    if (contextMenu.identifiedParcelles.length > 0) {
      const firstParcelle = contextMenu.identifiedParcelles[0];
      const [lon, lat] = transform(contextMenu.coordinate!, "EPSG:3857", "EPSG:4326");
      
      return {
        noParcelle: firstParcelle.value,
        noCommune: firstParcelle.attributes?.['NO COMMUNE'] || '',
        commune: firstParcelle.attributes?.COMMUNE || '',
        coordinate: [lon, lat] as [number, number],
        // Add property data if available
        terrainAreaM2: propertyData?.terrain_area_m2,
        buildingType: propertyData?.building_type,
        buildingAreaM2: propertyData?.building_area_m2
      };
    }
    return undefined;
  };

  return (
    <>
      {/* Context Menu */}
      {contextMenu.visible && createPortal(
        <div
          data-context-menu
          style={{
            position: 'fixed',
            top: contextMenu.y,
            left: contextMenu.x,
            backgroundColor: 'white',
            border: '1px solid #ccc',
            borderRadius: 4,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            zIndex: 1000,
            minWidth: 180,
            maxWidth: 300,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Parcelle Info */}
          {contextMenu.identifiedParcelles.length > 0 && (
            <div style={{
              padding: '8px 16px',
              borderBottom: '1px solid #f0f0f0',
              fontSize: '12px',
              color: '#666',
              backgroundColor: '#f9f9f9'
            }}>
              <div style={{ fontWeight: 'bold', marginBottom: '4px', color: '#333' }}>
                Données de la parcelle
              </div>
              {contextMenu.identifiedParcelles.map((p: any, index: number) => (
                <div key={index} style={{ marginBottom: index < contextMenu.identifiedParcelles.length - 1 ? '6px' : 0 }}>
                  <div><strong>Numéro de parcelle:</strong> {p.value}</div>
                  {p.attributes?.COMMUNE && (
                    <div><strong>Commune:</strong> {p.attributes.COMMUNE}</div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Property Data Display */}
          {(loadingPropertyData || propertyData) && (
            <div style={{
              padding: '8px 16px',
              borderBottom: '1px solid #f0f0f0',
              fontSize: '12px',
              color: '#666',
              backgroundColor: '#f9f9f9'
            }}>
              {loadingPropertyData ? (
                <div style={{ fontStyle: 'italic' }}>Chargement des données...</div>
              ) : propertyData ? (
                <div>
                  <div style={{ fontWeight: 'bold', marginBottom: '4px', color: '#333' }}>
                    Données de la propriété
                  </div>
                  {propertyData.terrain_area_m2 && (
                    <div><strong>Terrain:</strong> {propertyData.terrain_area_m2.toLocaleString()} m²</div>
                  )}
                  {propertyData.building_type && (
                    <div><strong>Type:</strong> {propertyData.building_type}</div>
                  )}
                  {propertyData.building_area_m2 && (
                    <div><strong>Surface bât:</strong> {propertyData.building_area_m2.toLocaleString()} m²</div>
                  )}
                  {propertyData.data_sources.length > 0 && (
                    <div style={{ fontSize: '10px', marginTop: '4px', fontStyle: 'italic', color: '#888' }}>
                      Sources: {propertyData.data_sources.join(', ')}
                    </div>
                  )}
                  {!propertyData.terrain_area_m2 && !propertyData.building_type && !propertyData.building_area_m2 && (
                    <div style={{ fontStyle: 'italic', color: '#999' }}>
                      Aucune donnée disponible
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          )}

          <div
            style={{
              padding: '8px 16px',
              cursor: 'pointer',
            }}
            onClick={handleRegistreFoncier}
            onMouseEnter={(e) => {
              (e.target as HTMLElement).style.backgroundColor = '#f5f5f5';
            }}
            onMouseLeave={(e) => {
              (e.target as HTMLElement).style.backgroundColor = 'transparent';
            }}
          >
            {contextMenu.identifiedParcelles.length > 0 ? (
              'Voir dans le RF'
            ) : (
              'Aucune parcelle - Voir dans le RF'
            )}
          </div>
          
          {contextMenu.identifiedParcelles.length > 0 && (
            <div
              style={{
                padding: '8px 16px',
                cursor: loadingPropertyData ? 'not-allowed' : 'pointer',
                borderTop: '1px solid #f0f0f0',
                backgroundColor: loadingPropertyData ? '#f5f5f5' : 'transparent',
                color: loadingPropertyData ? '#999' : '#000',
                opacity: loadingPropertyData ? 0.6 : 1,
              }}
              onClick={loadingPropertyData ? undefined : handleCreateBienOffMarket}
              onMouseEnter={(e) => {
                if (!loadingPropertyData) {
                  (e.target as HTMLElement).style.backgroundColor = '#f5f5f5';
                }
              }}
              onMouseLeave={(e) => {
                if (!loadingPropertyData) {
                  (e.target as HTMLElement).style.backgroundColor = 'transparent';
                }
              }}
            >
              {loadingPropertyData ? (
                'Chargement des données...'
              ) : (
                'Créer bien off-market'
              )}
            </div>
          )}
        </div>,
        document.body
      )}
      
      {/* NewBienOffMarket modal - always rendered outside context menu */}
      {showNewBienOffMarket && (
        <NewBienOffMarket 
          parcelleData={savedParcelleData} 
          onClose={() => {
            setShowNewBienOffMarket(false);
            setSavedParcelleData(null);
          }}
        />
      )}
    </>
  );
};

// Hook for context menu functionality
export const useMapContextMenu = (
  map: React.MutableRefObject<any>,
  onAddIdentifiedParcelles: (parcelles: any[]) => void,
  onRemoveIdentifiedParcelles: () => void
) => {
  const [contextMenu, setContextMenu] = React.useState<ContextMenuState>({
    visible: false,
    x: 0,
    y: 0,
    coordinate: null,
    identifiedParcelles: [],
    sitgUrl: null,
  });

  // Function to handle context menu creation
  const createContextMenuHandler = React.useCallback(async (event: MouseEvent) => {
    event.preventDefault(); // Prevent browser's default context menu
    
    // Get the coordinate from the mouse position
    const pixel = map.current.getEventPixel(event);
    const coordinate = map.current.getCoordinateFromPixel(pixel);
    
    if (coordinate) {
      // Transform coordinate and identify parcelles immediately
      const [lon, lat] = transform(coordinate, "EPSG:3857", "EPSG:4326");
      
      try {
        // Call the backend endpoint to get filtered SITG data
        const response = await axiosInstance.get(
          `https://melcarnebackend.digipulse.ch/generate_sitg_identify_link?lon=${lon}&lat=${lat}&filter_by_point=true`
        );
        
        const identifiedParcelles = response.data?.data?.results || [];
        const sitgUrl = response.data?.url || null;
        
        // Add identified parcelles to the map
        onAddIdentifiedParcelles(identifiedParcelles);
        
        // Show context menu with identified data
        setContextMenu({
          visible: true,
          x: event.clientX,
          y: event.clientY,
          coordinate: coordinate,
          identifiedParcelles: identifiedParcelles,
          sitgUrl: sitgUrl,
        });
      } catch (error) {
        console.error('Error identifying parcelles:', error);
        // Show context menu anyway, but without identified parcelles
        setContextMenu({
          visible: true,
          x: event.clientX,
          y: event.clientY,
          coordinate: coordinate,
          identifiedParcelles: [],
          sitgUrl: null,
        });
      }
    }
  }, [map, onAddIdentifiedParcelles]);

  // Function to close context menu
  const closeContextMenu = React.useCallback(() => {
    setContextMenu({ 
      visible: false, 
      x: 0, 
      y: 0, 
      coordinate: null, 
      identifiedParcelles: [], 
      sitgUrl: null 
    });
    onRemoveIdentifiedParcelles();
  }, [onRemoveIdentifiedParcelles]);

  return {
    contextMenu,
    setContextMenu,
    createContextMenuHandler,
    closeContextMenu,
  };
};

export default MapContextMenu; 